'use client';

import { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useMarketplaceStore } from '@/lib/marketplace/store';
import {
  Package,
  MoreVertical,
  ExternalLink,
  Trash2,
  RefreshCw,
  ArrowUpCircle,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

const TYPE_LABELS: Record<string, string> = {
  agent_template: 'Agent',
  connector: 'Connector',
  skill: 'Skill',
  workflow: 'Workflow',
};

export function MyInstalls() {
  const navigate = useNavigate();
  const {
    myInstalls,
    installsLoading,
    loadMyInstalls,
    uninstall,
  } = useMarketplaceStore();

  useEffect(() => {
    loadMyInstalls();
  }, [loadMyInstalls]);

  const handleUninstall = async (listingId: string, name: string) => {
    if (confirm(`Are you sure you want to uninstall "${name}"?`)) {
      try {
        await uninstall(listingId);
      } catch (error) {
        console.error('Failed to uninstall:', error);
      }
    }
  };

  if (installsLoading && myInstalls.length === 0) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Package className="size-6" />
          My Installations
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage your installed marketplace items
        </p>
      </div>

      {/* Installs Table */}
      <Card>
        <CardContent className="pt-6">
          {myInstalls.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="size-12 mx-auto mb-4 opacity-50" />
              <p>No installations yet</p>
              <p className="text-sm">Browse the marketplace to find templates and connectors</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate({ to: '/marketplace' })}
              >
                Browse Marketplace
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Installed</TableHead>
                  <TableHead>Auto Update</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {myInstalls.map((install) => (
                  <TableRow key={install.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {install.listing?.icon_url ? (
                          <img
                            src={install.listing.icon_url}
                            alt=""
                            className="size-8 rounded object-cover"
                          />
                        ) : (
                          <div className="size-8 rounded bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                            {install.listing?.name?.charAt(0) || '?'}
                          </div>
                        )}
                        <div>
                          <p className="font-medium">
                            {install.listing?.name || 'Unknown'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {install.listing?.publisher_name}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {install.listing?.listing_type
                          ? TYPE_LABELS[install.listing.listing_type]
                          : 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      v{install.installed_version}
                      {install.listing?.version !== install.installed_version && (
                        <Badge variant="outline" className="ml-2 gap-1 text-xs">
                          <ArrowUpCircle className="size-3" />
                          v{install.listing?.version} available
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(install.installed_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={install.auto_update ? 'default' : 'secondary'}>
                        {install.auto_update ? 'On' : 'Off'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() =>
                              navigate({ to: '/marketplace/$slug', params: { slug: install.listing?.slug || '' } })
                            }
                          >
                            <ExternalLink className="size-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {install.listing?.version !== install.installed_version && (
                            <DropdownMenuItem>
                              <RefreshCw className="size-4 mr-2" />
                              Update
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() =>
                              handleUninstall(
                                install.listing_id,
                                install.listing?.name || 'this item'
                              )
                            }
                          >
                            <Trash2 className="size-4 mr-2" />
                            Uninstall
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
